/**
 * HelpTip — a small "?" that explains the control beside it, for someone new.
 *
 * It opens on hover, on keyboard focus, and on tap/click (which pins it open
 * until the next click, Escape, or focus moving away), so it works with a
 * mouse, a keyboard and a touch screen alike. The explanation is a real
 * `role="tooltip"` element referenced by `aria-describedby`, so screen readers
 * announce it with the button.
 *
 * Put it next to a field's `<label>`, never inside it: a button inside a label
 * would leak "?" into the field's accessible name.
 */

import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/** Keep the bubble this far from the window's edges. */
const EDGE_PX = 8;

export function HelpTip({ topic, children }: {
  /** What the tip is about, used in the button's accessible name ("What is <topic>?"). */
  topic: string;
  children: React.ReactNode;
}): React.ReactElement {
  const id = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);
  const shown = hovered || focused || pinned;

  // Centred under the "?" by default; nudged sideways when that would run off the window.
  useLayoutEffect(() => {
    if (!shown || !bubble.current) { setShift(0); return; }
    const box = bubble.current.getBoundingClientRect();
    const centred = box.left - shift;
    const width = box.width;
    const max = window.innerWidth - EDGE_PX;
    if (centred < EDGE_PX) setShift(EDGE_PX - centred);
    else if (centred + width > max) setShift(max - (centred + width));
    else setShift(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  useEffect(() => {
    if (!pinned) return undefined;
    const close = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setPinned(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pinned]);

  return (
    <span
      className="help-tip"
      ref={root}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className="help-tip-button"
        aria-label={`What is ${topic}?`}
        aria-expanded={shown}
        aria-describedby={shown ? id : undefined}
        onClick={() => setPinned((value) => !value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); setPinned(false); }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') { setPinned(false); setFocused(false); setHovered(false); }
        }}
      >
        ?
      </button>
      <span
        role="tooltip"
        id={id}
        ref={bubble}
        className="help-tip-bubble"
        hidden={!shown}
        style={shift ? { transform: `translateX(calc(-50% + ${shift}px))` } : undefined}
      >
        {children}
      </span>
    </span>
  );
}

export default HelpTip;
